import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BRIEF_NOTES_MAX_LENGTH,
  BRIEF_THEMES,
  type BriefStatus,
  type BriefTheme,
  type ProjectSettings,
} from '@gc/shared';
import { ApiError, apiRequest } from '../../api/client.js';
import { actualizarProyecto } from '../../api/projects.js';

export type PanelDirectorProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly settings: ProjectSettings;
  onCambio(): void;
};

/**
 * Director creativo: desarrolla la idea en un brief profesional. El trabajo lo
 * hace el worker; aquí se pide, se sigue por polling y se aplica si convence.
 * Aplicarlo cambia descripción, estilo y dirección musical, y por tanto sube la
 * revisión como cualquier otra edición.
 */
export function PanelDirector({
  projectId,
  revision,
  status,
  settings,
  onCambio,
}: PanelDirectorProps): React.JSX.Element {
  const [tema, setTema] = useState<BriefTheme>('otro');
  const [notas, setNotas] = useState('');
  const [estado, setEstado] = useState<BriefStatus | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [desplegado, setDesplegado] = useState(false);
  const avisar = useRef(onCambio);
  avisar.current = onCambio;

  const recargar = useCallback(async () => {
    try {
      setEstado(await apiRequest<BriefStatus>(`/projects/${projectId}/brief`));
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo consultar el brief.');
    }
  }, [projectId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const enCurso = estado?.status === 'PENDING' || estado?.status === 'RUNNING';
  useEffect(() => {
    if (!enCurso) return;
    const intervalo = setInterval(() => void recargar(), 3_000);
    return () => clearInterval(intervalo);
  }, [enCurso, recargar]);

  // Con brief listo o en curso, el panel se abre solo: hay algo que ver.
  useEffect(() => {
    if (estado !== null && estado.status !== 'NONE') setDesplegado(true);
  }, [estado]);

  async function pedir(): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await apiRequest(`/projects/${projectId}/brief`, {
        method: 'POST',
        body: { theme: tema, notes: notas },
      });
      await recargar();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo pedir el brief.');
    } finally {
      setOcupado(false);
    }
  }

  async function aplicar(): Promise<void> {
    if (estado?.brief === null || estado?.brief === undefined) return;
    setOcupado(true);
    setError(null);
    try {
      await actualizarProyecto(projectId, {
        expectedRevision: revision,
        description: estado.brief.developedIdea,
        settings: { style: estado.brief.visualStyle, musicPrompt: estado.brief.musicDirection },
      });
      avisar.current();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo aplicar el brief.');
    } finally {
      setOcupado(false);
    }
  }

  const bloqueado = ocupado || enCurso || status === 'GENERATING' || status === 'RENDERING';
  const brief = estado?.brief ?? null;
  const yaAplicado = brief !== null && settings.style === brief.visualStyle;

  return (
    <section
      aria-labelledby="titulo-director"
      style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}
    >
      <h2 id="titulo-director" style={{ marginTop: 0 }}>
        Director creativo
      </h2>
      <p style={{ marginTop: 0 }}>
        <small>
          Desarrolla la idea como lo haría un director creativo: concepto, público, tono, estilo
          visual, dirección musical y estructura. Luego decides si la aplicas al proyecto.
        </small>
      </p>

      {!desplegado ? (
        <button type="button" onClick={() => setDesplegado(true)}>
          Desarrollar la idea con el director creativo
        </button>
      ) : (
        <>
          <p>
            <label htmlFor="tema-brief">Temática</label>
            <br />
            <select
              id="tema-brief"
              value={tema}
              disabled={bloqueado}
              onChange={(evento) => setTema(evento.target.value as BriefTheme)}
            >
              {Object.entries(BRIEF_THEMES).map(([clave, etiqueta]) => (
                <option key={clave} value={clave}>
                  {etiqueta}
                </option>
              ))}
            </select>
          </p>
          <p>
            <label htmlFor="notas-brief">Indicaciones (público, mensaje, lo que no puede faltar)</label>
            <br />
            <textarea
              id="notas-brief"
              rows={3}
              maxLength={BRIEF_NOTES_MAX_LENGTH}
              value={notas}
              disabled={bloqueado}
              onChange={(evento) => setNotas(evento.target.value)}
              style={{ width: '100%', maxWidth: '40rem' }}
            />
          </p>
          <p>
            <button type="button" onClick={() => void pedir()} disabled={bloqueado}>
              {enCurso ? 'Desarrollando…' : brief ? 'Pedir otra propuesta' : 'Desarrollar la idea'}
            </button>
          </p>
        </>
      )}

      {enCurso && <p role="status">El director creativo está trabajando. Esto llama al proveedor de texto.</p>}
      {estado?.status === 'FAILED' && (
        <p role="alert">El brief falló: {estado.error ?? 'sin detalle'}. Puedes volver a pedirlo.</p>
      )}

      {brief !== null && (
        <article style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', marginTop: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>{brief.title}</h3>
          <p>
            <em>{brief.logline}</em>
          </p>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
            <dt>Concepto</dt>
            <dd>{brief.concept}</dd>
            <dt>Público</dt>
            <dd>{brief.audience}</dd>
            <dt>Tono</dt>
            <dd>{brief.tone}</dd>
            <dt>Estilo visual</dt>
            <dd>{brief.visualStyle}</dd>
            <dt>Música</dt>
            <dd>{brief.musicDirection}</dd>
            <dt>Narración</dt>
            <dd>{brief.narrationDirection}</dd>
          </dl>
          <h4>Estructura propuesta</h4>
          <ol>
            {brief.outline.map((bloque, indice) => (
              <li key={`${indice}-${bloque.title}`}>
                <strong>{bloque.title}</strong> — {bloque.description}
              </li>
            ))}
          </ol>
          <h4>Idea desarrollada</h4>
          <p style={{ whiteSpace: 'pre-wrap' }}>{brief.developedIdea}</p>
          <p>
            <button type="button" onClick={() => void aplicar()} disabled={bloqueado || yaAplicado}>
              {yaAplicado ? 'Aplicado al proyecto' : 'Usar esta idea en el proyecto'}
            </button>{' '}
            <small>Sustituye la idea, el estilo y la dirección musical del proyecto; las escenas no cambian hasta volver a generar.</small>
          </p>
        </article>
      )}

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}
